import { useRouter } from "@tanstack/react-router"
import type { FormEvent, ReactNode } from "react"
import { useCallback, useEffect, useState } from "react"

import { accessStore } from "../../access/access-store"
import { verifyMfa } from "../../api/auth.api"
import type { ApiProblem } from "../../api/problem"
import { isApiProblem, transportProblem } from "../../api/problem"
import { nextNeedsNoTenant, safeNextPath } from "../../app/navigate-next"
import styles from "../auth.module.css"
import { mfaChallengeStore } from "./mfa-challenge"

/**
 * Второй фактор (Э1.2). Свой адрес, а не окно поверх формы входа: у шага есть своё
 * состояние, свой отказ и свой возврат назад, и всё это обязано жить в адресной строке.
 */
export function MfaScreen(props: { readonly next: string | undefined }): ReactNode {
  const router = useRouter()
  const challenge = mfaChallengeStore.read()
  const [code, setCode] = useState("")
  const [method, setMethod] = useState<"totp" | "recovery">("totp")
  const [problem, setProblem] = useState<ApiProblem | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    // Проверка не начата или уже истекла: возвращаемся к паролю, а не показываем
    // форму кода, которую всё равно нечем подтвердить.
    if (challenge === null) void router.navigate({ to: "/login", search: { next: props.next } })
  }, [challenge, props.next, router])

  const onSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>): Promise<void> => {
      event.preventDefault()
      if (challenge === null) return
      setBusy(true)
      setProblem(null)
      try {
        const response = await verifyMfa({
          mfaToken: challenge.mfaToken,
          code,
          method,
          // Поле контракта остаётся, значение — всегда false: сервер его не читает,
          // и присылать true значило бы записывать в журнал запросов обещание,
          // которого никто не исполняет.
          trustDevice: false,
        })
        if (response.status !== "authenticated") return

        mfaChallengeStore.clear()
        const next = await accessStore.reload()
        if (next.kind === "tenant_required" && !nextNeedsNoTenant(props.next)) {
          await router.navigate({ to: "/choose-salon", search: { next: props.next } })
          return
        }
        router.history.push(safeNextPath(props.next, "/"))
      } catch (error) {
        setProblem(isApiProblem(error) ? error : transportProblem("unknown"))
      } finally {
        setBusy(false)
      }
    },
    [challenge, code, method, props.next, router],
  )

  if (challenge === null) return null

  return (
    <div className={styles.page}>
      <form
        className={styles.card}
        onSubmit={(event) => {
          void onSubmit(event)
        }}
      >
        <div className={styles.brand}>planerius</div>
        <h1 className={styles.title}>Подтвердите вход</h1>
        <p className={styles.subtitle}>
          Введите код из приложения-аутентификатора. Код действует до{" "}
          {new Date(challenge.expiresAt).toLocaleTimeString("ru-RU", {
            hour: "2-digit",
            minute: "2-digit",
          })}
          .
        </p>

        {problem === null ? null : (
          <div className={styles.error} role="alert">
            <div className={styles.errorTitle}>{problem.title}</div>
            {problem.detail ?? "Проверьте код и попробуйте ещё раз."}
          </div>
        )}

        <div className={styles.field}>
          <label className={styles.label} htmlFor="mfa-code">
            Код подтверждения
          </label>
          <input
            id="mfa-code"
            className={styles.input}
            inputMode="numeric"
            autoComplete="one-time-code"
            autoFocus
            required
            value={code}
            onChange={(event) => {
              setCode(event.target.value)
            }}
          />
        </div>

        {challenge.methods.includes("recovery") ? (
          <div className={styles.field}>
            <label className={styles.label} htmlFor="mfa-method">
              Способ
            </label>
            <select
              id="mfa-method"
              className={styles.input}
              value={method}
              onChange={(event) => {
                setMethod(event.target.value === "recovery" ? "recovery" : "totp")
              }}
            >
              <option value="totp">Код из приложения</option>
              <option value="recovery">Резервный код</option>
            </select>
          </div>
        ) : null}

        {/*
          * «ЗАПОМНИТЬ ЭТО УСТРОЙСТВО» УБРАНО, И ЭТО НЕ УПРОЩЕНИЕ ЭКРАНА. Поле trustDevice
          * есть в контракте, экран его отправлял — а сервер не читает вовсе: ни строки
          * доверенного устройства, ни пропуска кода на следующем входе. То есть галочка
          * обещала «больше не спрашивать» и не делала ничего, а человек, поверивший ей,
          * считал повторный запрос кода поломкой. Вернётся вместе с механизмом:
          * доверенное устройство — это запись в базе со сроком и отзывом, а не флаг
          * в запросе входа.
          */}

        <button className={styles.submit} type="submit" disabled={busy}>
          {busy ? "Проверяем…" : "Подтвердить"}
        </button>
      </form>
    </div>
  )
}
